import { metabolicThresholds } from '../data/internalMedicine';

export interface FraminghamParams {
  age: number;
  sex: 'male' | 'female';
  totalCholesterol: number;
  hdl: number;
  sbp: number;
  smoking: boolean;
  diabetes: boolean;
  treated: boolean;
}

export interface FraminghamResult {
  risk_percent: number;
  category: 'bajo' | 'moderado' | 'alto' | 'muy_alto';
  data_complete: boolean;
  missing_factors: string[];
}

export interface MetabSyndromeParams {
  waist_cm: number | null;
  triglycerides: number | null;
  hdl: number | null;
  sbp: number | null;
  dbp: number | null;
  fasting_glucose: number | null;
  sex: 'male' | 'female';
}

export interface MetabSyndromeCriterion {
  id: string;
  name: string;
  positive: boolean;
}

export interface MetabSyndromeResult {
  count: number;
  diagnosis: boolean;
  criteria: MetabSyndromeCriterion[];
}

// ─── Framingham ATP III Point-Based System ───────────────────────────
// Fuente: ATP III / Framingham 2002 (Wilson et al., Circulation 1998)
// Sistema de puntuación estándar usado en clínica.

// Edad en grupos (índice: 0=20-34, 1=35-39, 2=40-44, 3=45-49,
//   4=50-54, 5=55-59, 6=60-64, 7=65-69, 8=70-74, 9=75-79)
function getAgeIndex(age: number): number {
  if (age < 35) return 0;
  if (age < 40) return 1;
  if (age < 45) return 2;
  if (age < 50) return 3;
  if (age < 55) return 4;
  if (age < 60) return 5;
  if (age < 65) return 6;
  if (age < 70) return 7;
  if (age < 75) return 8;
  return 9;
}

// Puntos por edad según sexo
function agePoints(age: number, sex: 'male' | 'female'): number {
  const idx = getAgeIndex(age);
  const malePoints = [-9, -4, 0, 3, 6, 8, 10, 11, 12, 13];
  const femalePoints = [-7, -3, 0, 3, 6, 8, 10, 12, 14, 16];
  return sex === 'male' ? malePoints[idx] : femalePoints[idx];
}

// Mapea getAgeIndex (0-9) a fila de tabla ATP III (0-4)
// idx: 0-1→20-39, 2-3→40-49, 4-5→50-59, 6-7→60-69, 8-9→70-79
function ageIdxToTableRow(idx: number): number {
  return Math.min(Math.floor(idx / 2), 4);
}

// Puntos por colesterol total según edad y sexo
function tcPoints(tc: number, age: number, sex: 'male' | 'female'): number {
  const idx = getAgeIndex(age);
  const cat = tc < 160 ? 0 : tc < 200 ? 1 : tc < 240 ? 2 : tc < 280 ? 3 : 4;

  const maleTable: number[][] = [
    [0, 4, 8, 11, 14],  // 20-39
    [0, 3, 6, 8, 10],   // 40-49
    [0, 2, 4, 5, 7],    // 50-59
    [0, 1, 2, 3, 4],    // 60-69
    [0, 0, 1, 1, 2],    // 70-79
  ];
  const femaleTable: number[][] = [
    [0, 4, 9, 14, 18],  // 20-39
    [0, 3, 6, 8, 11],   // 40-49
    [0, 2, 4, 5, 8],    // 50-59
    [0, 1, 2, 3, 4],    // 60-69
    [0, 1, 1, 2, 2],    // 70-79
  ];

  const tableIdx = ageIdxToTableRow(idx);
  return sex === 'male' ? maleTable[tableIdx][cat] : femaleTable[tableIdx][cat];
}

// Puntos por tabaquismo según edad y sexo
function smokingPoints(smoking: boolean, age: number, sex: 'male' | 'female'): number {
  if (!smoking) return 0;
  const idx = getAgeIndex(age);
  // 10 valores, uno por cada idx (20-34, 35-39, 40-44, 45-49, 50-54, 55-59, 60-64, 65-69, 70-74, 75-79)
  const maleSmoking = [8, 8, 5, 5, 3, 3, 1, 1, 1, 1];
  const femaleSmoking = [9, 9, 7, 7, 4, 4, 2, 2, 1, 1];
  return sex === 'male' ? maleSmoking[idx] : femaleSmoking[idx];
}

// Puntos por HDL (igual para ambos sexos en el point system ATP III)
function hdlPoints(hdl: number): number {
  if (hdl >= 60) return -1;
  if (hdl >= 50) return 0;
  if (hdl >= 40) return 1;
  return 2;
}

// Puntos por PAS según tratamiento y sexo
function sbpPoints(sbp: number, treated: boolean, sex: 'male' | 'female'): number {
  const cat = sbp < 120 ? 0 : sbp < 130 ? 1 : sbp < 140 ? 2 : sbp < 160 ? 3 : 4;

  const maleUntreated = [0, 0, 1, 1, 2];
  const maleTreated = [0, 1, 2, 2, 3];
  const femaleUntreated = [0, 1, 2, 3, 4];
  const femaleTreated = [0, 2, 3, 4, 5];

  if (sex === 'male') {
    return treated ? maleTreated[cat] : maleUntreated[cat];
  }
  return treated ? femaleTreated[cat] : femaleUntreated[cat];
}

// Riesgo a 10 años según puntos totales
function riskFromPoints(points: number, sex: 'male' | 'female'): { percent: number; category: FraminghamResult['category'] } {
  // Tablas de riesgo ATP III
  const maleRisk: Record<number, number> = {
    [-9]: 0.5, [-8]: 0.5, [-7]: 0.5, [-6]: 0.5, [-5]: 0.5, [-4]: 0.5, [-3]: 0.5, [-2]: 0.5, [-1]: 0.5,
    [0]: 0.5, [1]: 1, [2]: 1, [3]: 1, [4]: 1, [5]: 2, [6]: 2, [7]: 3, [8]: 4, [9]: 5, [10]: 6,
    [11]: 8, [12]: 10, [13]: 12, [14]: 16, [15]: 20, [16]: 25, [17]: 30,
  };
  const femaleRisk: Record<number, number> = {
    [-9]: 0.5, [-8]: 0.5, [-7]: 0.5, [-6]: 0.5, [-5]: 0.5, [-4]: 0.5, [-3]: 0.5, [-2]: 0.5, [-1]: 0.5,
    [0]: 0.5, [1]: 0.5, [2]: 1, [3]: 1, [4]: 1, [5]: 2, [6]: 2, [7]: 3, [8]: 4, [9]: 5, [10]: 6,
    [11]: 8, [12]: 9, [13]: 11, [14]: 13, [15]: 15, [16]: 18, [17]: 20, [18]: 24, [19]: 27, [20]: 30,
  };

  const riskTable = sex === 'male' ? maleRisk : femaleRisk;
  const clampedPoints = Math.max(Math.min(points, 20), -9);
  const percent = riskTable[clampedPoints] ?? (points > 20 ? 30 : 0.5);
  const clampedPercent = Math.min(percent, 30);

  let category: FraminghamResult['category'];
  if (clampedPercent <= 10) category = 'bajo';
  else if (clampedPercent <= 20) category = 'moderado';
  else if (clampedPercent <= 30) category = 'alto';
  else category = 'muy_alto';

  return { percent: clampedPercent, category };
}

export function classifyHTAStage(
  sbp: number,
  dbp: number
): { stage: string; label: string } {
  // ACC/AHA 2017 guidelines — cascading thresholds
  if (sbp >= 180 || dbp >= 120) {
    return { stage: 'crisis', label: 'Crisis Hipertensiva' };
  }
  if (sbp >= 140 || dbp >= 90) {
    return { stage: 'stage2', label: 'Estadio 2' };
  }
  if (sbp >= 130 || dbp >= 80) {
    return { stage: 'stage1', label: 'Estadio 1' };
  }
  if (sbp >= 120 && dbp < 80) {
    return { stage: 'elevated', label: 'Elevada' };
  }
  return { stage: 'normal', label: 'Normal' };
}

export function calculateFraminghamRisk(
  params: FraminghamParams
): FraminghamResult {
  const missing_factors: string[] = [];
  if (params.age == null || params.age < 20 || params.age > 79) {
    missing_factors.push('Edad (debe ser 20-79 años)');
  }
  if (params.totalCholesterol == null) missing_factors.push('Colesterol Total');
  if (params.hdl == null) missing_factors.push('HDL');
  if (params.sbp == null) missing_factors.push('Presión Arterial Sistólica');

  if (missing_factors.length > 0) {
    return {
      risk_percent: 0,
      category: 'bajo',
      data_complete: false,
      missing_factors,
    };
  }

  const pts =
    agePoints(params.age, params.sex) +
    tcPoints(params.totalCholesterol, params.age, params.sex) +
    smokingPoints(params.smoking, params.age, params.sex) +
    hdlPoints(params.hdl) +
    sbpPoints(params.sbp, params.treated, params.sex) +
    (params.diabetes ? (params.sex === 'male' ? 2 : 4) : 0);

  const { percent, category } = riskFromPoints(pts, params.sex);

  return {
    risk_percent: percent,
    category,
    data_complete: true,
    missing_factors: [],
  };
}

export function checkMetabolicSyndrome(
  params: MetabSyndromeParams
): MetabSyndromeResult {
  const thresholds = metabolicThresholds;
  const waistThreshold = params.sex === 'male' ? thresholds.waist_cm.male : thresholds.waist_cm.female;
  const hdlThreshold = params.sex === 'male' ? thresholds.hdl.male : thresholds.hdl.female;

  const criteria: MetabSyndromeCriterion[] = [
    {
      id: 'waist',
      name: 'Perímetro Abdominal',
      positive: params.waist_cm != null && params.waist_cm > waistThreshold,
    },
    {
      id: 'triglycerides',
      name: 'Triglicéridos',
      positive: params.triglycerides != null && params.triglycerides >= thresholds.triglycerides,
    },
    {
      id: 'hdl',
      name: 'HDL',
      positive: params.hdl != null && params.hdl < hdlThreshold,
    },
    {
      id: 'blood_pressure',
      name: 'Presión Arterial',
      positive:
        (params.sbp != null && params.sbp >= thresholds.sbp) ||
        (params.dbp != null && params.dbp >= thresholds.dbp),
    },
    {
      id: 'fasting_glucose',
      name: 'Glucemia en Ayunas',
      positive: params.fasting_glucose != null && params.fasting_glucose >= thresholds.fasting_glucose,
    },
  ];

  const count = criteria.filter((c) => c.positive).length;

  return {
    count,
    diagnosis: count >= 3,
    criteria,
  };
}
